/**
 * ShareableLineageStory v2 — The Viral Growth Engine
 *
 * "Look how a recipe became a typeface."
 *
 * This is the single most important page for growth. When someone shares
 * a lineage link, the person who clicks it needs to have a jaw-drop moment
 * within the first 2 seconds. The zone transitions, the visual evolution,
 * the "how did THAT become THAT?" — it all has to land immediately.
 *
 * Two exports:
 *   ShareLineageButton  — inline share trigger for the scrubber / tray
 *   LineageStoryPage    — standalone page at /lineage/:rootId (PUBLIC)
 *
 * Backend: GET /api/lineage/:rootId/story (no auth required)
 *
 * Place at: src/components/journey/ShareableLineageStory.jsx
 */

import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Share2, Copy, Check, ExternalLink, Link2,
  ArrowRight, Sparkles, Clock, Users, GitFork,
  ChevronDown, ChevronUp, Eye, Zap, Layers,
  AlertCircle, RefreshCw, Home, Play, Pause,
  ArrowUpRight, UserPlus,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { ZONES } from '../../config/zones';
import { API_ORIGIN } from '../../config/apiBase';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const AUTO_PLAY_INTERVAL = 3500;
const TRANSITION_PAUSE   = 5000; // Extra pause on cross-zone transitions

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function buildShareUrl(rootProjectId) {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/lineage/${rootProjectId}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return ''; }
}

function daySpanLabel(a, b) {
  if (!a || !b) return '';
  const ms = Math.abs(new Date(b) - new Date(a));
  const days = Math.round(ms / 86_400_000);
  if (days === 0) return 'same day';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''}`;
  const years = Math.round(months / 12);
  return `${years} year${years > 1 ? 's' : ''}`;
}

function computeStoryData(projects) {
  if (!projects || projects.length === 0) return null;

  const first = projects[0];
  const last  = projects[projects.length - 1];

  const zones = projects.map(p => p.zone || p.category || 'smart');
  const uniqueZones = [...new Set(zones)];

  const uniqueCreators = [...new Set(
    projects.map(p =>
      p.author?.username || p.user?.username || p.username || 'unknown'
    ),
  )];

  const transitions = [];
  for (let i = 1; i < projects.length; i++) {
    if (zones[i] !== zones[i - 1]) {
      transitions.push({
        from: zones[i - 1],
        to: zones[i],
        fromProject: projects[i - 1],
        toProject: projects[i],
        index: i,
      });
    }
  }

  const firstDate = first.createdAt || first.created_at;
  const lastDate  = last.createdAt  || last.created_at;
  const span      = daySpanLabel(firstDate, lastDate);

  let headline;
  if (transitions.length > 0) {
    const fromLabel = ZONES[zones[0]]?.shortLabel || zones[0];
    const toLabel   = ZONES[zones[zones.length - 1]]?.shortLabel || zones[zones.length - 1];
    headline = `${fromLabel} \u2192 ${toLabel}`;
  } else {
    const zoneLabel = ZONES[zones[0]]?.shortLabel || 'creative';
    headline = `${projects.length} generations of ${zoneLabel}`;
  }

  const firstName = first.name || 'a project';
  const lastName  = last.name  || 'something new';
  let story;
  if (transitions.length > 0) {
    story = `\u201C${firstName}\u201D became \u201C${lastName}\u201D, crossing ${transitions.length} creative discipline${transitions.length > 1 ? 's' : ''} along the way.`;
  } else {
    story = `\u201C${firstName}\u201D evolved into \u201C${lastName}\u201D through ${projects.length - 1} fork${projects.length > 2 ? 's' : ''}.`;
  }

  return {
    headline, story, projects, transitions,
    uniqueZones, uniqueCreators, span,
    firstProject: first, lastProject: last,
    totalSteps: projects.length,
    crossZoneCount: transitions.length,
  };
}

function buildShareText(storyData) {
  if (!storyData) return '';
  return `${storyData.headline} \u2014 ${storyData.story} See the full creative evolution on Enume.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// SMALL SHARED UI ATOMS
// ─────────────────────────────────────────────────────────────────────────────

const ZonePill = ({ zoneId, size = 'sm' }) => {
  const zone = ZONES[zoneId] || {};
  const dotCls = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{
        color:           zone.color || '#a1a1aa',
        borderColor:     (zone.color || '#71717a') + '30',
        backgroundColor: (zone.color || '#71717a') + '10',
        border:          `1px solid ${(zone.color || '#71717a')}30`,
      }}
    >
      <span
        className={cn('rounded-full flex-shrink-0', dotCls)}
        style={{ backgroundColor: zone.color || '#71717a' }}
      />
      {zone.shortLabel || zoneId}
    </span>
  );
};

const StatBadge = ({ icon: Icon, value, label, highlight }) => (
  <div className={cn(
    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
    highlight
      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
      : 'bg-zinc-800/80 text-zinc-400 border border-zinc-700/40',
  )}>
    <Icon className="w-3.5 h-3.5" />
    <span className="font-semibold">{value}</span>
    {label && <span className="text-zinc-500">{label}</span>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// COVER IMAGE with graceful fallback
// ─────────────────────────────────────────────────────────────────────────────

const CoverImage = ({ src, alt, zone, className }) => {
  const [errored, setErrored] = useState(false);
  const zoneObj = ZONES[zone] || {};

  if (!src || errored) {
    return (
      <div
        className={cn('flex items-center justify-center', className)}
        style={{
          background: `linear-gradient(135deg, ${(zoneObj.color || '#6366f1')}20, ${(zoneObj.color || '#6366f1')}08)`,
        }}
      >
        <span className="text-3xl font-black opacity-20" style={{ color: zoneObj.color || '#6366f1' }}>
          {zoneObj.shortLabel?.[0] || '?'}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-cover', className)}
      loading="lazy"
      onError={() => setErrored(true)}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ZONE TRANSITION MOMENT — the "aha" between cards
// ─────────────────────────────────────────────────────────────────────────────

const ZoneTransitionMoment = ({ from, to, isVisible }) => {
  const fromZone = ZONES[from] || {};
  const toZone   = ZONES[to] || {};

  return (
    <div className={cn(
      'flex items-center justify-center py-4 transition-all duration-700',
      isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
    )}>
      <div className="relative flex items-center gap-3 px-5 py-2.5 rounded-2xl bg-zinc-900/90 border border-zinc-700/50 shadow-xl">
        {/* Glow behind */}
        <div
          className="absolute inset-0 rounded-2xl opacity-30 blur-xl"
          style={{
            background: `linear-gradient(90deg, ${fromZone.color || '#71717a'}40, ${toZone.color || '#71717a'}40)`,
          }}
        />

        <div className="relative flex items-center gap-3">
          {/* From zone */}
          <div className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-full shadow-lg"
              style={{
                backgroundColor: fromZone.color || '#71717a',
                boxShadow: `0 0 12px ${fromZone.color || '#71717a'}60`,
              }}
            />
            <span className="text-xs font-bold" style={{ color: fromZone.color || '#a1a1aa' }}>
              {fromZone.shortLabel || from}
            </span>
          </div>

          {/* Arrow with gradient */}
          <div className="flex items-center">
            <div
              className="w-8 h-0.5 rounded-full"
              style={{
                background: `linear-gradient(90deg, ${fromZone.color || '#71717a'}, ${toZone.color || '#71717a'})`,
              }}
            />
            <Zap className="w-4 h-4 text-amber-400 -ml-1" />
          </div>

          {/* To zone */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold" style={{ color: toZone.color || '#a1a1aa' }}>
              {toZone.shortLabel || to}
            </span>
            <span
              className="w-3 h-3 rounded-full shadow-lg"
              style={{
                backgroundColor: toZone.color || '#71717a',
                boxShadow: `0 0 12px ${toZone.color || '#71717a'}60`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT CARD — the timeline card
// ─────────────────────────────────────────────────────────────────────────────

const StoryProjectCard = ({ project, stepNumber, total, isActive, isTransition, onProjectClick }) => {
  const zone     = ZONES[project.zone || project.category || 'smart'] || {};
  const coverSrc = project.coverImage || project.cover_image || project.image_url;
  const author   = project.author?.username || project.user?.username || project.username || 'unknown';
  const date     = project.createdAt || project.created_at;

  return (
    <div
      className={cn(
        'group relative rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer',
        'border bg-zinc-900/60 backdrop-blur-sm',
        isActive
          ? 'border-zinc-600 shadow-2xl scale-[1.01]'
          : 'border-zinc-800/50 hover:border-zinc-700 hover:shadow-xl',
      )}
      style={isActive ? {
        boxShadow: `0 8px 40px -12px ${zone.color || '#6366f1'}30, 0 4px 20px -4px rgba(0,0,0,0.5)`,
      } : {}}
      onClick={() => onProjectClick?.(project)}
    >
      {/* Zone accent bar at top */}
      <div
        className="h-1 w-full transition-opacity duration-300"
        style={{
          background: `linear-gradient(90deg, ${zone.color || '#6366f1'}, ${zone.color || '#6366f1'}60)`,
          opacity: isActive || isTransition ? 1 : 0.4,
        }}
      />

      <div className="flex flex-col sm:flex-row">
        {/* Cover image */}
        {coverSrc && (
          <div className="sm:w-48 h-44 sm:h-auto overflow-hidden flex-shrink-0">
            <CoverImage
              src={coverSrc}
              alt={project.name}
              zone={project.zone}
              className="w-full h-full transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-5">
          {/* Top row: zone pill + step counter */}
          <div className="flex items-center justify-between mb-3">
            <ZonePill zoneId={project.zone || 'smart'} />
            <span className="text-[10px] text-zinc-600 font-mono tabular-nums">
              {stepNumber}/{total}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base font-bold text-white mb-1.5 leading-snug group-hover:text-zinc-100 transition-colors">
            {project.name}
          </h3>

          {/* Description */}
          {project.description && (
            <p className="text-[13px] text-zinc-400 leading-relaxed line-clamp-2 mb-3">
              {project.description}
            </p>
          )}

          {/* Tags */}
          {project.tags && project.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {project.tags.slice(0, 5).map(tag => (
                <span
                  key={typeof tag === 'string' ? tag : tag.name || tag}
                  className="text-[10px] px-2 py-0.5 rounded-md bg-zinc-800/70 text-zinc-500 border border-zinc-700/30"
                >
                  {typeof tag === 'string' ? tag : tag.name || tag}
                </span>
              ))}
              {project.tags.length > 5 && (
                <span className="text-[10px] px-2 py-0.5 rounded-md bg-zinc-800/50 text-zinc-600">
                  +{project.tags.length - 5}
                </span>
              )}
            </div>
          )}

          {/* Footer: author + stats */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 font-medium">
              @{author}
            </span>
            <div className="flex items-center gap-3 text-[11px] text-zinc-600">
              {(project.forkCount > 0 || project.fork_count > 0) && (
                <span className="flex items-center gap-1">
                  <GitFork className="w-3 h-3" />
                  {project.forkCount || project.fork_count}
                </span>
              )}
              {(project.viewCount > 0 || project.view_count > 0) && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {project.viewCount || project.view_count}
                </span>
              )}
              {date && <span>{formatDate(date)}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Hover arrow */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowUpRight className="w-4 h-4 text-zinc-500" />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-PARENT INDICATOR
// ─────────────────────────────────────────────────────────────────────────────

const MultiParentNotice = ({ parentIds }) => {
  if (!parentIds || parentIds.length <= 1) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-500/8 border border-indigo-500/15 text-[11px] text-indigo-300/80 mb-2">
      <Layers className="w-3.5 h-3.5 flex-shrink-0" />
      Merged ideas from {parentIds.length} parent projects
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HERO — Zone journey visualization at the top
// ─────────────────────────────────────────────────────────────────────────────

const ZoneJourneyHero = ({ uniqueZones, transitions }) => {
  const hasCrossings = transitions && transitions.length > 0;

  return (
    <div className="flex items-center justify-center flex-wrap gap-2 mb-8">
      {uniqueZones.map((z, i) => {
        const zObj = ZONES[z] || {};
        return (
          <React.Fragment key={`${z}-${i}`}>
            <div className="flex flex-col items-center gap-1.5 group">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black transition-transform duration-300 group-hover:scale-110"
                style={{
                  background: `linear-gradient(135deg, ${zObj.color || '#6366f1'}25, ${zObj.color || '#6366f1'}10)`,
                  border: `1.5px solid ${zObj.color || '#6366f1'}40`,
                  color: zObj.color || '#a1a1aa',
                  boxShadow: `0 4px 16px ${zObj.color || '#6366f1'}15`,
                }}
              >
                {zObj.shortLabel?.[0] || '?'}
              </div>
              <span
                className="text-[10px] font-semibold tracking-wide"
                style={{ color: zObj.color || '#71717a' }}
              >
                {zObj.shortLabel || z}
              </span>
            </div>
            {i < uniqueZones.length - 1 && (
              <div className="flex items-center -mt-5">
                <div
                  className="w-6 h-0.5 rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${ZONES[uniqueZones[i]]?.color || '#71717a'}60, ${ZONES[uniqueZones[i + 1]]?.color || '#71717a'}60)`,
                  }}
                />
                <ArrowRight className="w-3.5 h-3.5 text-zinc-600 -ml-0.5" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESS TIMELINE — mini horizontal scrubber
// ─────────────────────────────────────────────────────────────────────────────

const ProgressTimeline = ({ projects, activeIndex, onSelect }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current.children[activeIndex];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIndex]);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2 px-1"
      >
        {projects.map((p, i) => {
          const zone = ZONES[p.zone || 'smart'] || {};
          const isActive = i === activeIndex;
          const prevZone = i > 0 ? (projects[i - 1].zone || 'smart') : null;
          const isCrossing = prevZone && prevZone !== (p.zone || 'smart');

          return (
            <React.Fragment key={p.id || i}>
              {isCrossing && (
                <Zap className="w-3 h-3 text-amber-400 flex-shrink-0 mx-0.5" />
              )}
              <button
                onClick={() => onSelect(i)}
                className={cn(
                  'flex-shrink-0 rounded-full transition-all duration-300',
                  isActive ? 'w-8 h-3 shadow-lg' : 'w-3 h-3 hover:scale-125',
                )}
                style={{
                  backgroundColor: isActive ? zone.color : `${zone.color || '#71717a'}40`,
                  boxShadow: isActive ? `0 0 12px ${zone.color}50` : 'none',
                }}
                title={p.name}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-PLAY CONTROLS
// ─────────────────────────────────────────────────────────────────────────────

const PlaybackControls = ({ isPlaying, onToggle, currentIndex, total }) => (
  <div className="flex items-center gap-3">
    <button
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all',
        isPlaying
          ? 'bg-purple-500/15 text-purple-300 border border-purple-500/25 shadow-lg shadow-purple-500/10'
          : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 border border-zinc-700/50',
      )}
    >
      {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      {isPlaying ? 'Pause' : 'Watch the evolution'}
    </button>
    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{
          width: `${total > 1 ? ((currentIndex + 1) / total) * 100 : 100}%`,
          background: 'linear-gradient(90deg, #a855f7, #6366f1)',
        }}
      />
    </div>
    <span className="text-[11px] text-zinc-600 font-mono tabular-nums w-10 text-right">
      {currentIndex + 1}/{total}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHARE PANEL — reused by button and page header
// ─────────────────────────────────────────────────────────────────────────────

const SharePanel = ({ storyData, shareUrl, onClose, className }) => {
  const [copied, setCopied] = useState(false);
  const shareText = buildShareText(storyData);

  const handleCopy = useCallback(async () => {
    const payload = `${shareText}\n${shareUrl}`;
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = payload;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [shareText, shareUrl]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Creative Evolution: ${storyData?.headline}`,
          text:  shareText,
          url:   shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      handleCopy();
    }
  }, [storyData, shareText, shareUrl, handleCopy]);

  if (!storyData) return null;

  return (
    <div className={cn(
      'bg-zinc-900/98 backdrop-blur-xl border border-zinc-700/60 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden',
      className,
    )}>
      {/* Preview card */}
      <div className="p-5 bg-gradient-to-br from-zinc-800/40 to-zinc-900/60">
        <div className="flex items-center justify-center flex-wrap gap-1.5 mb-4">
          {storyData.uniqueZones.slice(0, 5).map((z, i) => {
            const zObj = ZONES[z] || {};
            return (
              <React.Fragment key={`${z}-${i}`}>
                <span
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-[9px] font-bold"
                  style={{
                    background: `${zObj.color || '#6366f1'}20`,
                    border: `1px solid ${zObj.color || '#6366f1'}30`,
                    color: zObj.color || '#a1a1aa',
                  }}
                >
                  {zObj.shortLabel?.[0] || '?'}
                </span>
                {i < Math.min(storyData.uniqueZones.length, 5) - 1 && (
                  <ArrowRight className="w-2.5 h-2.5 text-zinc-600" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <h4 className="text-sm font-bold text-white mb-1 text-center">{storyData.headline}</h4>
        <p className="text-[11px] text-zinc-400 leading-relaxed text-center mb-3 line-clamp-2">
          {storyData.story}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <StatBadge icon={GitFork} value={storyData.totalSteps} label="steps" />
          {storyData.crossZoneCount > 0 && (
            <StatBadge icon={Zap} value={storyData.crossZoneCount} label="crossings" highlight />
          )}
          <StatBadge icon={Users} value={storyData.uniqueCreators.length} label="creators" />
        </div>
      </div>

      {/* Share actions */}
      <div className="p-3 space-y-0.5">
        <button
          onClick={handleCopy}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-medium hover:bg-zinc-800 transition-colors"
        >
          {copied
            ? <Check className="w-3.5 h-3.5 text-emerald-400" />
            : <Link2 className="w-3.5 h-3.5 text-zinc-400" />}
          <span className={copied ? 'text-emerald-400' : 'text-zinc-300'}>
            {copied ? 'Copied!' : 'Copy link'}
          </span>
        </button>

        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />
            Share via\u2026
          </button>
        )}

        <button
          onClick={() => {
            const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
            window.open(url, '_blank', 'width=550,height=420');
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12px] font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-zinc-400 fill-current">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          Post to X
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARE LINEAGE BUTTON — inline, for scrubber / tray / overlays
// ─────────────────────────────────────────────────────────────────────────────

export const ShareLineageButton = ({ projects, className }) => {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);
  const storyData       = useMemo(() => computeStoryData(projects), [projects]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!storyData || storyData.totalSteps < 2) return null;

  const shareUrl = buildShareUrl(storyData.firstProject.id);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen(v => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all',
          'bg-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-700 border border-zinc-700/50',
          open && 'bg-zinc-700 text-white',
        )}
      >
        <Share2 className="w-3.5 h-3.5" />
        Share this journey
      </button>

      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 w-80 z-50 animate-shareIn"
        >
          <SharePanel storyData={storyData} shareUrl={shareUrl} />
        </div>
      )}

      <style>{`
        @keyframes shareIn {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-shareIn { animation: shareIn 0.18s ease-out forwards; }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OG META INJECTOR
// ─────────────────────────────────────────────────────────────────────────────

function useOGMeta({ title, description, imageUrl }) {
  useEffect(() => {
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (title)       document.title = title;
    if (title)       setMeta('og:title', title, true);
    if (description) setMeta('og:description', description, true);
    if (description) setMeta('description', description);
    if (imageUrl)    setMeta('og:image', imageUrl, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');
    if (title)       setMeta('twitter:title', title);
    if (description) setMeta('twitter:description', description);
    if (imageUrl)    setMeta('twitter:image', imageUrl);
  }, [title, description, imageUrl]);
}

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETON
// ─────────────────────────────────────────────────────────────────────────────

const StoryLoadingSkeleton = () => (
  <div className="min-h-screen bg-zinc-950 text-white">
    <div className="sticky top-0 z-10 h-14 bg-zinc-950/95 border-b border-zinc-800/50 backdrop-blur-xl" />
    <div className="max-w-2xl mx-auto px-4 py-12 animate-pulse">
      {/* Hero placeholder */}
      <div className="text-center mb-12 space-y-4">
        <div className="flex justify-center gap-4 mb-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-zinc-800" />
              <div className="w-12 h-2 rounded bg-zinc-800" />
            </div>
          ))}
        </div>
        <div className="h-10 w-72 rounded-2xl bg-zinc-800 mx-auto" />
        <div className="h-5 w-96 rounded-lg bg-zinc-800/70 mx-auto" />
        <div className="flex justify-center gap-3 mt-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-8 w-24 rounded-full bg-zinc-800" />
          ))}
        </div>
      </div>
      {/* Card placeholders */}
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-2xl bg-zinc-900/60 border border-zinc-800 overflow-hidden">
            <div className="h-1 w-full bg-zinc-800" />
            <div className="flex">
              <div className="w-48 h-36 bg-zinc-800 hidden sm:block" />
              <div className="flex-1 p-5 space-y-3">
                <div className="h-6 w-20 rounded-full bg-zinc-800" />
                <div className="h-5 w-48 rounded bg-zinc-800" />
                <div className="h-4 w-full rounded bg-zinc-800/60" />
                <div className="h-4 w-3/4 rounded bg-zinc-800/60" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ERROR STATE
// ─────────────────────────────────────────────────────────────────────────────

const StoryErrorState = ({ message, onRetry }) => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
    <div className="text-center max-w-sm">
      <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <AlertCircle className="w-8 h-8 text-zinc-600" />
      </div>
      <h2 className="text-lg font-bold text-white mb-2">Couldn't load this story</h2>
      <p className="text-sm text-zinc-500 mb-6 leading-relaxed">
        {message || 'The lineage may have been deleted or made private.'}
      </p>
      <div className="flex items-center justify-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-300 text-sm font-medium hover:bg-zinc-700 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        )}
        <a
          href="/"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors shadow-lg shadow-purple-600/20"
        >
          <Home className="w-3.5 h-3.5" />
          Explore the Map
        </a>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// LINEAGE STORY PAGE — standalone, self-fetching, public
// ─────────────────────────────────────────────────────────────────────────────

export const LineageStoryPage = () => {
  const { rootId } = useParams();
  const navigate   = useNavigate();

  const [status, setStatus]         = useState('loading');
  const [errMsg, setErrMsg]         = useState('');
  const [chain, setChain]           = useState([]);
  const [meta, setMeta]             = useState(null);
  const [showShare, setShowShare]   = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [visibleCards, setVisibleCards] = useState(new Set());

  const cardRefs  = useRef([]);
  const playTimer = useRef(null);
  const shareRef  = useRef(null);

  // ── Intersection observer for staggered card reveal ─────────────────────
  useEffect(() => {
    if (chain.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            if (!isNaN(idx)) {
              setVisibleCards(prev => new Set([...prev, idx]));
              setActiveIndex(idx); // Track scroll position
            }
          }
        });
      },
      { threshold: 0.3, rootMargin: '-10% 0px -20% 0px' },
    );

    cardRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [chain.length]);

  // ── Close share panel on outside click ──────────────────────────────────
  useEffect(() => {
    if (!showShare) return;
    const handler = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showShare]);

  // ── Data fetch ──────────────────────────────────────────────────────────
  const fetchStory = useCallback(async () => {
    if (!rootId) { setStatus('error'); setErrMsg('No project ID provided.'); return; }
    setStatus('loading');
    try {
      // Deliberately raw fetch, not the shared client: this page is public and
      // the client's 401 interceptor redirects to /login, which would bounce
      // logged-out visitors arriving from a shared link.
      const res = await fetch(`${API_ORIGIN}/api/lineage/${rootId}/story`, {
        headers: { Accept: 'application/json' },
      });

      if (res.status === 404) {
        setStatus('error');
        setErrMsg('This creative journey no longer exists or has been made private.');
        return;
      }
      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const data = await res.json();
      setChain(data.chain || []);
      setMeta({
        crossZoneCount:     data.crossZoneCount || 0,
        uniqueCreatorCount: data.uniqueCreatorCount || 1,
        uniqueZones:        data.uniqueZones || [],
        totalSteps:         data.totalSteps || (data.chain || []).length,
      });
      setStatus('ok');
    } catch (e) {
      console.error('LineageStoryPage fetch error:', e);
      setStatus('error');
      setErrMsg(e.message || 'Something went wrong loading this story.');
    }
  }, [rootId]);

  useEffect(() => { fetchStory(); }, [fetchStory]);

  // ── Auto-play ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying || chain.length < 2) return;

    const advance = () => {
      setActiveIndex(i => {
        const next = i + 1;
        if (next >= chain.length) {
          setIsPlaying(false);
          return i;
        }
        cardRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setVisibleCards(prev => new Set([...prev, next]));

        // Check if next card is a zone transition — pause longer
        const currZone = chain[i]?.zone || 'smart';
        const nextZone = chain[next]?.zone || 'smart';
        if (currZone !== nextZone) {
          clearInterval(playTimer.current);
          playTimer.current = setTimeout(() => {
            playTimer.current = setInterval(advance, AUTO_PLAY_INTERVAL);
            advance();
          }, TRANSITION_PAUSE - AUTO_PLAY_INTERVAL);
        }

        return next;
      });
    };

    playTimer.current = setInterval(advance, AUTO_PLAY_INTERVAL);
    return () => {
      clearInterval(playTimer.current);
      clearTimeout(playTimer.current);
    };
  }, [isPlaying, chain]);

  // ── Compute story data ─────────────────────────────────────────────────
  const storyData = useMemo(() => computeStoryData(chain), [chain]);

  // ── OG meta tags ───────────────────────────────────────────────────────
  useOGMeta({
    title:       storyData ? `${storyData.headline} \u2014 Enume` : 'Creative Lineage \u2014 Enume',
    description: storyData?.story,
    imageUrl:    chain[0]?.coverImage,
  });

  // ── Render gates ───────────────────────────────────────────────────────
  if (status === 'loading') return <StoryLoadingSkeleton />;
  if (status === 'error')   return <StoryErrorState message={errMsg} onRetry={fetchStory} />;
  if (!storyData)           return <StoryErrorState message="No story data found." onRetry={fetchStory} />;

  const shareUrl = buildShareUrl(rootId);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">

      {/* ── Sticky nav ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 h-14 flex items-center justify-between px-5 bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800/50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-medium"
        >
          <Home className="w-4 h-4" />
          <span className="hidden sm:inline">Explore the Map</span>
        </button>

        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 group"
        >
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/10 group-hover:shadow-purple-500/25 transition-shadow">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-bold text-white hidden sm:inline">Enume</span>
        </button>

        <div className="relative" ref={shareRef}>
          <button
            onClick={() => setShowShare(v => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all',
              showShare
                ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                : 'bg-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-700 border border-zinc-700/50',
            )}
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
          {showShare && (
            <div className="absolute right-0 top-full mt-2 w-80 z-50 animate-shareIn">
              <SharePanel storyData={storyData} shareUrl={shareUrl} />
            </div>
          )}
        </div>
      </header>

      {/* ── Hero section ────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        {/* Ambient glow behind hero */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {storyData.uniqueZones.slice(0, 3).map((z, i) => {
            const zObj = ZONES[z] || {};
            return (
              <div
                key={z}
                className="absolute rounded-full blur-3xl opacity-[0.07]"
                style={{
                  backgroundColor: zObj.color,
                  width: '400px', height: '400px',
                  left: `${20 + i * 30}%`,
                  top: `${30 + i * 15}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            );
          })}
        </div>

        <div className="relative max-w-2xl mx-auto px-4 pt-14 pb-10">
          <ZoneJourneyHero
            uniqueZones={storyData.uniqueZones}
            transitions={storyData.transitions}
          />

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-center mb-4 leading-[1.1] tracking-tight">
            {storyData.headline}
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 text-center max-w-lg mx-auto leading-relaxed">
            {storyData.story}
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
            <StatBadge icon={GitFork} value={storyData.totalSteps} label="steps" />
            {storyData.crossZoneCount > 0 && (
              <StatBadge icon={Zap} value={storyData.crossZoneCount} label="crossings" highlight />
            )}
            <StatBadge icon={Users} value={storyData.uniqueCreators.length} label={storyData.uniqueCreators.length > 1 ? 'creators' : 'creator'} />
            {storyData.span && (
              <StatBadge icon={Clock} value={storyData.span} />
            )}
          </div>

          {/* Playback + progress */}
          {chain.length > 1 && (
            <div className="mt-8 space-y-3">
              <PlaybackControls
                isPlaying={isPlaying}
                onToggle={() => setIsPlaying(v => !v)}
                currentIndex={activeIndex}
                total={chain.length}
              />
              <ProgressTimeline
                projects={chain}
                activeIndex={activeIndex}
                onSelect={(i) => {
                  setActiveIndex(i);
                  setIsPlaying(false);
                  cardRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setVisibleCards(prev => new Set([...prev, i]));
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Timeline ────────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 pb-20">
        <div className="relative">
          {/* Vertical spine */}
          <div className="absolute left-6 top-0 bottom-0 w-px pointer-events-none">
            <div className="w-full h-full bg-gradient-to-b from-zinc-700 via-zinc-800 to-transparent" />
          </div>

          {chain.map((project, i) => {
            const prevZone  = i > 0 ? (chain[i - 1].zone || 'smart') : null;
            const currZone  = project.zone || 'smart';
            const isTransition = prevZone && prevZone !== currZone;
            const zone = ZONES[currZone] || {};
            const isCardVisible = visibleCards.has(i);

            return (
              <div
                key={project.id || i}
                ref={el => { cardRefs.current[i] = el; }}
                data-index={i}
                className="relative pl-16 pb-8 last:pb-0"
              >
                {/* Spine dot */}
                <div
                  className={cn(
                    'absolute left-4 top-6 w-5 h-5 rounded-full border-[3px] border-zinc-950 z-10 transition-all duration-500',
                    activeIndex === i && 'scale-125',
                  )}
                  style={{
                    backgroundColor: zone.color || '#71717a',
                    boxShadow: activeIndex === i
                      ? `0 0 16px ${zone.color || '#71717a'}50, 0 0 4px ${zone.color || '#71717a'}30`
                      : 'none',
                  }}
                />

                {/* Zone transition moment */}
                {isTransition && (
                  <ZoneTransitionMoment
                    from={prevZone}
                    to={currZone}
                    isVisible={isCardVisible}
                  />
                )}

                {/* Multi-parent notice */}
                {project.parentProjectIds && project.parentProjectIds.length > 1 && (
                  <div className="mb-2">
                    <MultiParentNotice parentIds={project.parentProjectIds} />
                  </div>
                )}

                {/* The card — with staggered entrance */}
                <div
                  className="transition-all duration-700 ease-out"
                  style={{
                    opacity:   isCardVisible ? 1 : 0,
                    transform: isCardVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                  }}
                >
                  <StoryProjectCard
                    project={project}
                    stepNumber={i + 1}
                    total={chain.length}
                    isActive={activeIndex === i}
                    isTransition={isTransition}
                    onProjectClick={(p) => navigate(`/dashboard?project=${p.id}`)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── CTA footer ──────────────────────────────────────────────────── */}
      <div className="relative border-t border-zinc-800/60 overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-purple-600/5 blur-3xl" />
        </div>

        <div className="relative max-w-lg mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Enume
          </div>

          <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight">
            Every project can spark something unexpected
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base mb-8 max-w-sm mx-auto leading-relaxed">
            See how creative ideas connect, combine, and evolve across disciplines. Your next inspiration might come from the most surprising place.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-semibold text-sm transition-all shadow-xl shadow-purple-600/25 hover:shadow-purple-500/35"
            >
              <UserPlus className="w-4 h-4" />
              Join Enume
            </a>
            <a
              href="/"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl font-semibold text-sm transition-colors border border-zinc-700/50"
            >
              Explore the Map
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* ── Inline styles ───────────────────────────────────────────────── */}
      <style>{`
        @keyframes shareIn {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-shareIn { animation: shareIn 0.18s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ShareLineageButton;